import { Hours, max, min, subtract } from "@pentacode/openapi";
import { BreakMode, CompanySettings, TimeEntry, TimeSettings } from "./model";
import { Interval, TimeResultMode, hour } from "./time";

/**
 * Specifies thresholds and amount of automatic breaks.
 */
export interface AutoBreak {
    duration: Hours;
    amount: Hours;
}

export interface BreakDuration {
    auto: Hours;
    manual: Hours;
}

/**
 * Calculates the break time for a given work duration required by law.
 *
 * @param shiftDuration - The duration of the shift.
 * @param iterativeBreaks - If true, the break is subtracted from the given duration before calculating the break.
 */
export function getStatutoryBreak(
    shiftDuration: Hours,
    { iterativeBreaks = false }: Partial<CompanySettings> = {}
): Hours {
    if (shiftDuration <= 6) {
        return 0 as Hours;
    } else if (shiftDuration <= (iterativeBreaks ? 9.5 : 9)) {
        return 0.5 as Hours;
    } else {
        return 0.75 as Hours;
    }
}

/**
 * Get the automatic break for a given duration. The thresholds can be defined individually using {@link TimeSettings.autoBreaks} but default to the
 * statuatory breaks in standardMode.
 *
 * @returns
 */
export function getAutomaticBreak(shiftDuration: Hours, { breakMode, autoBreaks = [] }: Partial<TimeSettings>): Hours {
    if (breakMode === BreakMode.Manual) {
        return 0 as Hours;
    }
    return max(0 as Hours, ...autoBreaks.filter((b) => b.duration < shiftDuration).map((b) => b.amount));
}

function calculateBreakDurationsPlanned(
    entry: Readonly<TimeEntry>,
    timeSettings: Readonly<TimeSettings>,
    duration: Hours,
    iterativeBreaks: boolean
): BreakDuration {
    let auto: Hours;

    if (entry.breakAuto != null) {
        auto = entry.breakAuto;
    } else if (timeSettings.breakMode === BreakMode.Manual) {
        auto = getStatutoryBreak(duration, { iterativeBreaks });
    } else if ([BreakMode.Planned, BreakMode.PlannedPlusManual].includes(timeSettings.breakMode)) {
        auto = entry.breakPlanned || (0 as Hours);
    } else {
        auto = getAutomaticBreak(duration, timeSettings);
    }

    return { manual: 0 as Hours, auto };
}

export function calculateBreakDurationsFinalized(
    entry: Readonly<TimeEntry>,
    timeSettings: Readonly<TimeSettings>,
    duration: Hours
) {
    const breakFinal = entry.break || (0 as Hours);
    let auto: Hours;

    if (entry.breakAuto != null) {
        auto = entry.breakAuto;
    } else if ([BreakMode.Planned, BreakMode.PlannedPlusManual].includes(timeSettings.breakMode)) {
        auto = entry.breakPlanned || (0 as Hours);
    } else {
        auto = min(breakFinal, getAutomaticBreak(duration, timeSettings));
    }

    const manual = subtract(breakFinal, auto);

    return { manual, auto };
}

export function calculateBreakDurations(
    entry: Readonly<TimeEntry>,
    timeSettings: Readonly<TimeSettings>,
    timeResultMode: TimeResultMode,
    duration: Hours,
    iterativeBreaks: boolean
): BreakDuration {
    if (timeResultMode === "planned" || (timeResultMode === "mixed" && !entry.isPast && !entry.final)) {
        return calculateBreakDurationsPlanned(entry, timeSettings, duration, iterativeBreaks);
    }

    return calculateBreakDurationsFinalized(entry, timeSettings, duration);
}
